import  { useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import PropTypes from "prop-types";
// Google Drive Image URLs
const imageUrls = {
  myppimage: "https://lh3.googleusercontent.com/d/1aeZO0Dcn3eW2Qo4S3sdnqpABrVGt2wcP",
  littleStarSchool: "https://lh3.googleusercontent.com/d/1ooNUOE26s2k3wJqd_q9ShMhC8w-nKHx_",
  technicalEducationCollege: "https://lh3.googleusercontent.com/d/168TR65yN0Tp1av0D9NnOwy8YCWN9vCiN",
  rkuniversity: "https://lh3.googleusercontent.com/d/1oNbBDZ6U__w8zhjZvWuwhx0ACrlR7qDi",
  reading: "https://lh3.googleusercontent.com/d/1oRImD9KPciWCAl4CZLp3BoD7lJPnB9jp",
  running: "https://lh3.googleusercontent.com/d/1LNcp0ZUhKDhp4Ggx8ig62Kyk2JcjxqRk",
  cooking: "https://lh3.googleusercontent.com/d/1b18vipidgOY1o98Wc5eau8wyFpaLx0F0",
  movies: "https://lh3.googleusercontent.com/d/1DYHgScYmac0YmmB_sp_xUaxho-tumpiz",
  netflix: "https://lh3.googleusercontent.com/d/1LznEZv_D6KepVD3YNS8HmABOXAnyu1Wm",
  coding: "https://lh3.googleusercontent.com/d/1yBrCYPloWIOpZF5PK2_tyYvg_VDrOF9C",
  traveling: "https://lh3.googleusercontent.com/d/1ZN_CVSEJZfOkORb2SzFkediaXPpYbQXg"
};

import Footer from "../components/Footer";

// Preload images function
const preloadImages = (imageArray) => {
  return Promise.all(
    imageArray.map((src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = resolve; // Resolve on error to avoid blocking
      });
    })
  );
};

const AboutPage = ({ children }) => {
  const { darkMode } = useTheme();
  useEffect(() => {
    document.title = "About Me | Bhishan Sah - Software Engineer";

    // Set meta description
    const metaDescription = document.createElement("meta");
    metaDescription.name = "description";
    metaDescription.content = "Here is my document certificate of my education";
    document.head.appendChild(metaDescription);

    // Preload images
    const loadImages = async () => {
      await preloadImages([
        imageUrls.myppimage,
        imageUrls.littleStarSchool,
        imageUrls.technicalEducationCollege,
        imageUrls.rkuniversity,
        imageUrls.reading,
        imageUrls.running,
        imageUrls.cooking,
        imageUrls.movies,
        imageUrls.netflix,
        imageUrls.coding,
        imageUrls.traveling,
      ]);
    };

    loadImages();

    return () => {
      document.head.removeChild(metaDescription);
    };
  }, []);

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const AnimatedSection = ({ children }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false });

    return (
      <motion.div
        ref={ref}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={sectionVariants}
        transition={{ duration: 0.5 }}
      >
        {children}
      </motion.div>
    );
  };



  return (
    <div>
      <div className="lg:w-[85%] w-[90%] mx-auto select-none">
      
          <>
            <AnimatedSection>
              <div className="w-full mx-auto mb-8">
                <div className="flex items-center justify-center gap-4 w-full">
                  <div className="h-1 w-[30%] lg:w-[45%] bg-blue-500/30 rounded-full"></div>
                  <h1 className="lg:text-3xl w-[30%] lg:w-[20%] text-center text-md font-bold ">
                    ABOUT <span className="text-blue-500">ME</span>
                  </h1>
                  <div className="h-1 w-[30%] lg:w-[45%] bg-blue-500/30 rounded-full "></div>
                </div>
                <div>
                  <p className="lg:text-sm text-[8px] text-center">
                    ALLOW ME TO INTRODUCE MYSELF
                  </p>
                </div>

                {/* Introduction */}
                <div className={`rounded-2xl border overflow-hidden mt-4 ${darkMode ? 'bg-gradient-to-r from-gray-800/80 to-gray-700/60 border-white/10' : 'bg-gradient-to-r from-white to-blue-50/60 border-black/10'} shadow-lg`}>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-0 items-center">
                    <div className="md:col-span-2 flex justify-center md:justify-start p-6 relative">
                      <div className="relative">
                        <div className={`absolute -inset-1 rounded-3xl blur-lg ${darkMode ? 'bg-blue-400/20' : 'bg-blue-500/20'}`}></div>
                        <img
                          className="relative w-56 h-56 md:w-64 md:h-64 object-cover rounded-3xl ring-4 ring-blue-500/30"
                          src="https://lh3.googleusercontent.com/d/1aeZO0Dcn3eW2Qo4S3sdnqpABrVGt2wcP"
                          alt="Profile"
                          loading="eager"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-3 p-6">
                      <h1 className="text-2xl font-extrabold mb-3 flex items-center gap-3">
                        Introduction
                        <span className={`inline-block w-16 h-[3px] rounded-full ${darkMode ? 'bg-color-white' : 'bg-color-black '}`}></span>
                      </h1>
                      <p className={`mb-4 text-justify leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Welcome to my portfolio! I’m Bhishan Sah, a computer engineer and web developer based in Nepal. With a passion for technology and a strong foundation in software development, I create dynamic and user-friendly web applications. My approach combines technical expertise with innovative design, ensuring that each project is not only functional but also visually appealing.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className={`text-xs px-3 py-1 rounded-full ${darkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-600/10 text-green-700'}`}>Full-Stack Developer</span>
                        <span className={`text-xs px-3 py-1 rounded-full ${darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-600/10 text-purple-700'}`}>UI/UX Enthusiast</span>
                        <span className={`text-xs px-3 py-1 rounded-full ${darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-600/10 text-blue-700'}`}>Problem Solver</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Education Section */}
            <h2 className="text-2xl font-extrabold mb-4">
              Education
              <div className={`w-24 h-[3px] rounded-full ${darkMode ? 'bg-color-white' : 'bg-color-black '}`}></div>
            </h2>
            <div className="relative">
              <div className={`absolute left-4 top-0 bottom-0 w-[2px] ${darkMode ? 'bg-white/10' : 'bg-black/10'} hidden sm:block`}></div>
              {[
                {
                  title: "10th Class",
                  institution:
                    "Little Star Higher Secondary English Boarding School",
                  location: "Lahan-7, Siraha, Nepal, pincode - 56500",
                  major: "Science & English",
                  passedYear: "2016 A.D",
                  gradePercentage: "67%",
                  gpa: "2.68",
                  image: imageUrls.littleStarSchool,
                  CertificatePDf: "https://drive.google.com/file/d/1gDqlQ13R0FVQPr2YtyKLN9HPCoRYNuKB/preview"
                },
                {
                  title: "Diploma in Mechanical Engineering",
                  institution:
                    'Srm Global Institute of Technical Education "HSBTE BOARD"',
                  location:
                    "Village-Bhurewala, Naraingarh, Ambala, Haryana, pincode - 134203",
                  major: "Drawing & AutoCAD",
                  passedYear: "2019 A.D",
                  gradePercentage: "65.5%",
                  gpa: "N/A",
                  image: imageUrls.technicalEducationCollege,
                  website: "https://hsbte.org.in/",
                  CertificatePDf: "https://drive.google.com/file/d/1n_5sFJCvmTX7mDug6d3r6-C-487oEYUQ/preview"
                },
                {
                  title: "B-Tech in Computer Engineering",
                  institution: "RK University",
                  location: "Village-Tramba, Rajkot, Gujarat, pincode - 360020",
                  major: "Software Engineering",
                  passedYear: "2025 A.D",
                  gradePercentage: "76.9%",
                  gpa: "7.69",
                  image: imageUrls.rkuniversity,
                  website: "https://rku.ac.in/",
                  CertificatePDf: "https://drive.google.com/file/d/1fWzd51Zu6CeETviInFyuDrk7HAf9Xf0F/preview"
                },
              ].map((edu, index) => (
                <AnimatedSection key={index}>
                  <div className="relative pl-12 sm:pl-16 mb-8">
                    <div className={`absolute left-0 sm:left-3 top-1/2 h-3 w-3 rounded-full ring-4 ${darkMode ? 'bg-blue-400 ring-blue-400/20' : 'bg-blue-600 ring-blue-600/20'} hidden sm:block`}></div>
                    <div className={`group rounded-2xl overflow-hidden border backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${darkMode ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-white/70 border-black/10 hover:bg-white'} `}>
                      <div className="grid grid-cols-1 sm:grid-cols-7 gap-0">
                        <div className="sm:col-span-2 relative">
                          <img
                            className="h-full w-full object-cover sm:min-h-[160px]"
                            src={edu.image}
                            alt={edu.title}
                            loading="lazy"
                          />
                          <div className={`absolute inset-0 bg-gradient-to-t from-black/20 to-transparent ${darkMode ? '' : ''}`}></div>
                          <div className="absolute bottom-2 left-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-black/60 text-white/80' : 'bg-white/80 text-black/80'}`}>{edu.passedYear}</span>
                          </div>
                        </div>
                        <div className="sm:col-span-5 p-4 sm:p-6 flex flex-col gap-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md ${darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-600/10 text-blue-700'}`}>{edu.title}</span>
                            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md ${darkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-600/10 text-purple-700'}`}>{edu.major}</span>
                          </div>
                          <h3 className="text-lg font-bold leading-snug">{edu.institution}</h3>
                          <p className={`text-sm ${darkMode ? 'text-white/70' : 'text-black/60'}`}>{edu.location}</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                            <div className={`rounded-lg px-3 py-2 ${darkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                              <p className="text-xs opacity-70">Grade %</p>
                              <p className="text-sm font-semibold">{edu.gradePercentage}</p>
                            </div>
                            <div className={`rounded-lg px-3 py-2 ${darkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                              <p className="text-xs opacity-70">GPA</p>
                              <p className="text-sm font-semibold">{edu.gpa}</p>
                            </div>
                            {edu.website && (
                              <a
                                href={edu.website}
                                target="_blank"
                                rel="noreferrer"
                                className={`rounded-lg px-3 py-2 flex items-center justify-center text-sm font-medium transition-colors ${darkMode ? 'bg-blue-500/20 text-blue-200 hover:bg-blue-500/30' : 'bg-blue-600/10 text-blue-700 hover:bg-blue-600/20'}`}
                              >
                                Visit Website
                              </a>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-3">
                            {edu.CertificatePDf && (
                              <a
                                href={edu.CertificatePDf}
                                target="_blank"
                                rel="noreferrer"
                                className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${darkMode ? 'bg-yellow-400/90 text-black hover:bg-yellow-400' : 'bg-yellow-400 text-black hover:bg-yellow-500'}`}
                              >
                                View Certificate
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            {/* Experience and Certificates Section */}
            <AnimatedSection>
              <div
                className={`lg:flex justify-between gap-8 shadow-md rounded-lg px-4 lg:px-8 py-3 ${
                  darkMode
                    ? "bg-gradient-to-r to-color-white/50 from-gray-700/70"
                    : "bg-gradient-to-r to-color-black/40 from-color-blue/15 "
                }`}
              >
                <div className="mb-8 w-full">
                  <h2 className="text-2xl font-extrabold mb-4">
                    EXPERIENCE
                    <div className={`w-24 h-[3px] rounded-full ${darkMode ? 'bg-color-white' : 'bg-color-black '}`}></div>
                  </h2>
                  <div
                    className={`text-justify flex flex-col gap-3 mb-4 rounded-2xl p-6 lg:text-md text-[14px] border ${
                      darkMode ? "bg-color-black/70 text-color-white/80 border-white/10" : " bg-white/80 text-color-black/80 border-black/10"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-blue-500"></span>
                      <p><strong>1 Year</strong> experience in user account management at Vianet Communication Pvt. Ltd.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-blue-500"></span>
                      <p><strong>2 Years</strong> of field experience with knowledge of wiring, connection & layout designing.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-blue-500"></span>
                      <p><strong>4 Months</strong> of experience as supervisor at Shree Krishna Distribution Janakpur.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-blue-500"></span>
                      <p><strong>16 Weeks</strong> of experience as React with Node.js at SIGN SOFT TECH International PVT. Ltd, New Baneshwar Kathmandu.</p>
                    </div>
                  </div>
                </div>

                {/* Certificates Section */}
                <div className="w-full">
                  <h2 className="text-2xl font-extrabold mb-2">
                    CERTIFICATES
                    <div className={`w-24 h-[3px] rounded-full ${darkMode ? 'bg-color-white' : 'bg-color-black '}`}></div>
                  </h2>
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-2xl p-4 border ${
                      darkMode ? 'bg-color-black/70 text-color-white/80 border-white/10' : 'bg-white/80 text-color-black/80 border-black/10'
                    }`}
                  >
                    {[
                      { name: 'CISCO Networking' },
                      { name: 'Java Language Features' },
                      { name: 'Java Programming Fundamentals' },
                      { name: 'Java SE 8 Features' }
                    ].map((cert, i) => (
                      <div key={i} className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 transition-all hover:-translate-y-[2px] hover:shadow ${darkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10'}`}>
                        <div className="flex items-center gap-3">
                          <span className="h-8 w-8 rounded-lg bg-blue-600/15 text-blue-600 flex items-center justify-center text-sm">🎓</span>
                          <p className="text-sm font-medium">{cert.name}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-[2px] rounded-full ${darkMode ? 'bg-blue-500/20 text-blue-200' : 'bg-blue-600/10 text-blue-700'}`}>Certified</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Last Section */}
            <AnimatedSection>
              <div
                className={`w-full my-6 flex flex-wrap justify-between shadow-lg rounded-lg px-4 lg:px-8 py-3 ${
                  darkMode
                    ? "bg-gradient-to-l to-color-white/50 from-gray-700/70"
                    : "bg-gradient-to-l to-color-black/40 from-color-blue/15 "
                }`}
              >
                {/* Things I Love Section */}
                <div>
                  <h1 className="text-2xl font-bold mb-2">
                    THINGS I LOVE
                    <div className={`w-24 h-[3px] rounded-full ${darkMode ? 'bg-color-white' : 'bg-color-black '}`}></div>
                  </h1>
                  <div
                    className={`grid grid-cols-3 sm:grid-cols-7 gap-3 rounded-xl p-3 ${
                      darkMode ? "bg-color-black/70 text-color-white/70 " : " bg-white/70 text-color-black/70 "
                    }`}
                  >
                    {[
                      imageUrls.reading,
                      imageUrls.running,
                      imageUrls.cooking,
                      imageUrls.movies,
                      imageUrls.netflix,
                      imageUrls.coding,
                      imageUrls.traveling,
                    ].map((image, index) => (
                      <div key={index} className={`aspect-square rounded-xl overflow-hidden ring-1 ${darkMode ? 'ring-white/10' : 'ring-black/10'} bg-black/5 hover:scale-[1.02] transition-all`}>
                        <img
                          className="w-full p-4 rounded-full h-full object-cover"
                          src={image}
                          alt="Hobby"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Skills Section */}
                <div className="mt-4 w-full">
                  <h1 className="text-2xl font-bold mb-3 ">
                    LOOK WHAT I CAN DO
                    <div className={`w-24 h-[3px] rounded-full ${darkMode ? 'bg-color-white' : 'bg-color-black '}`}></div>
                  </h1>
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 w-full p-3 gap-3 rounded-xl ${
                      darkMode ? "bg-color-black/70 text-color-white/80 " : " bg-white/80 text-color-black/80 "
                    }`}
                  >
                    {[
                      "Web Designing",
                      "UI / UX Designing",
                      "Familiar with React/Vite",
                      "Familiar with Laravel",
                      "App Development: Flutter & React Native",
                      "PhotoShop PSD Designing",
                      "Video Editing",
                      "API Integrity & Creating APIs",
                      "Full Stack Web Development MERN",
                    ].map((skill, index) => (
                      <div key={index} className={`flex items-center justify-between rounded-xl px-4 py-3 border transition-all hover:-translate-y-[2px] ${darkMode ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-black/5 border-black/10 hover:bg-black/10'}`}>
                        <div className="flex items-center gap-3">
                          <span className={`h-6 w-6 rounded-md flex items-center justify-center text-xs ${darkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-600/10 text-green-700'}`}>✓</span>
                          <p className="text-sm font-medium">{skill}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-[2px] rounded-full ${darkMode ? 'bg-purple-500/20 text-purple-200' : 'bg-purple-600/10 text-purple-700'}`}>Skill</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </>
      </div>
      <Footer darkMode={darkMode} />
    </div>
  );
};

AboutPage.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AboutPage;
